import {
    h, Component, ComponentType, createContext, RefObject, createRef
} from "preact";
import { useContext } from "preact/hooks";

import { shallowDiffer } from "../../../foundation/utils/diff";
import { scrollIfNecessary, clearNativeSelection } from "../../../foundation/utils/platform";
import throttle from "../../../foundation/utils/throttle";
import { getKeyboardInfo, selectItemsInRange } from "../utils/navigation";
import { MultiSelectItem } from "../types";
import { withMultiSelectItem, MultiSelectGroup, WithMultiSelectItemProps, SelectionInfo } from "..";

// #region - Types

type MultiSelectGridItem = MultiSelectItem<number>;

// #endregion

// #region - MultiSelectGrid

interface MultiSelectGridProps {
    class?: string;
    selectedItems?: number[];
    numberOfItems: number;
    indexIdRecord?: Record<number, string>;
    scrollOffset?: number;
    navigationDelay?: number;
    horizontalScroll?: boolean;
    disableMultiselect?: boolean;
    onSelectionUpdate?: (selection: Set<number>) => void;
    onSelectionOpen?: (ev: KeyboardEvent, selection: Set<number>) => void;
    onScrollToUnmountedItem?: (index: number) => void;
    getScrollContainer: () => HTMLElement | null;
    onSelectionStart?: () => void;
    onSelectionEnd?: () => void;
}

interface MultiSelectGridState {
    selection: Set<number>;
    lastStartIndex: number | null;
}

interface MultiSelectGridHasSelectionState extends MultiSelectGridState {
    lastStartIndex: number;
}

class MultiSelectGrid extends Component<MultiSelectGridProps, MultiSelectGridState> {
    multiSelectGroupRef: RefObject<MultiSelectGroup<number>> = createRef();
    keyboardNavigationEnabled = false;

    constructor(props: MultiSelectGridProps) {
        super(props);

        this.state = {
            selection: new Set(props.selectedItems),
            lastStartIndex: props.selectedItems?.[0] ?? null
        };
    }

    get navigationDelay() {
        return this.props.navigationDelay ?? 0;
    }

    get registeredItems() {
        return this.multiSelectGroupRef.current!.registryMap;
    }

    getItemAtIndex = (index: number): MultiSelectGridItem | null => this.registeredItems[index] ?? null;

    getRowIdOfItem = (item: MultiSelectGridItem) => {
        const { getScrollContainer } = this.props;
        // We expect items to have node but in some cases node can be null.
        // We couldn't deterministically reproduce it, so we're just keeping this check here for now.
        if (!item.node) {
            return null;
        }

        const itemRect = item.node.getBoundingClientRect();
        const scrollContainer = getScrollContainer();

        return Math.ceil(itemRect.top + scrollContainer!.scrollTop);
    };

    getHitCoordinateOfItem = (item: MultiSelectGridItem) => {
        const { x, width } = item.node!.getBoundingClientRect();

        // eslint-disable-next-line no-magic-numbers
        return x + width / 2;
    };

    isHitCoordinateInsideItem = (item: MultiSelectGridItem, x: number) => {
        if (!item.node) {
            return false;
        }

        const { x: itemX, width } = item.node.getBoundingClientRect();
        return x >= itemX && x <= itemX + width;
    };

    handleSelectionDone = (selectedItems: number[], selectionInfo: SelectionInfo<number>) => {
        if (selectedItems.length === 0) {
            this.handleClearSelection();
            return;
        }

        const { onSelectionUpdate, getScrollContainer } = this.props;
        const { lastStartIndex } = this.state;
        const { firstSelectingItem } = selectionInfo;

        const updatedSelection = new Set(selectedItems);
        let updatedLastStartIndex = firstSelectingItem ?? lastStartIndex;

        if (lastStartIndex && !updatedSelection.has(lastStartIndex) && updatedSelection.size > 0) {
            updatedLastStartIndex = Math.min(...updatedSelection);
        }

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);
        getScrollContainer()?.focus();
    };

    handleClearSelection = () => {
        const { onSelectionUpdate } = this.props;

        const updatedSelection = new Set<number>();
        const updatedLastStartIndex = null;

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);
    };

    handleSelectAllItems = () => {
        const { numberOfItems, onSelectionUpdate } = this.props;
        const updatedSelection = new Set<number>();
        selectItemsInRange(updatedSelection, 0, numberOfItems - 1);

        this.setState({
            selection: updatedSelection,
            lastStartIndex: 0
        });

        onSelectionUpdate?.(updatedSelection);
    };

    handleUnselectItem = (index: number) => {
        const { onSelectionUpdate } = this.props;
        const { lastStartIndex, selection } = this.state;

        // Initialize updated state
        const updatedSelection = new Set(selection);
        let updatedLastStartIndex: number | null = lastStartIndex;

        // Unselect the item
        updatedSelection.delete(index);

        if (index === lastStartIndex && updatedSelection.size > 0) {
            updatedLastStartIndex = Math.min(...updatedSelection);
        } else if (updatedSelection.size === 0) {
            updatedLastStartIndex = null;
        }

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);
    };

    handleHorizontalNavigation = throttle((ev: KeyboardEvent) => {
        const { numberOfItems, disableMultiselect, onSelectionUpdate } = this.props;
        const { selection, lastStartIndex } = this.state as MultiSelectGridHasSelectionState;
        const { leftPressed, shiftPressed } = getKeyboardInfo(ev);

        let updatedSelection = new Set<number>();
        if (shiftPressed && !disableMultiselect) {
            updatedSelection = new Set(selection);
        }

        const direction = leftPressed ? -1 : 1;
        const nextIndex = lastStartIndex + direction;

        if (nextIndex < 0 || nextIndex >= numberOfItems) {
            // Out of bounds
            return;
        }

        const updatedLastStartIndex = nextIndex;
        updatedSelection.add(updatedLastStartIndex);

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);

        this.handleScrollToItem(updatedLastStartIndex);
    }, this.navigationDelay);

    // eslint-disable-next-line complexity
    handleVerticalNavigation = throttle((ev: KeyboardEvent) => {
        const { numberOfItems, disableMultiselect, onSelectionUpdate } = this.props;
        const { selection, lastStartIndex } = this.state as MultiSelectGridHasSelectionState;
        const { upPressed, downPressed, shiftPressed } = getKeyboardInfo(ev);

        let updatedSelection = new Set<number>();
        let updatedLastStartIndex = lastStartIndex;

        if (shiftPressed && !disableMultiselect) {
            updatedSelection = new Set(selection);
        }

        const currentItem = this.getItemAtIndex(lastStartIndex);
        if (currentItem === null) {
            // Current item is not mounted but there could be items to select in the navigation direction
            let from = lastStartIndex + 1;
            let to = numberOfItems - 1;
            let direction = 1;
            let numberOfItemsToSearch = to - from + 1;

            if (upPressed) {
                from = lastStartIndex - 1;
                to = 0;
                direction = -1;
                numberOfItemsToSearch = from - to + 1;
            }

            let closestMountedItem: number | null = null;

            // Find the first item that is mounted
            for (let i = 0; i < numberOfItemsToSearch; i++) {
                const index = from + i * direction;
                const item = this.getItemAtIndex(index);
                if (item && item.node !== null) {
                    closestMountedItem = index;
                    break;
                }
            }

            if (closestMountedItem !== null) {
                updatedSelection.add(closestMountedItem);
                updatedLastStartIndex = closestMountedItem;

                this.setState({
                    selection: updatedSelection,
                    lastStartIndex: updatedLastStartIndex
                });

                onSelectionUpdate?.(updatedSelection);
                this.handleScrollToItem(updatedLastStartIndex);
            }

            return;
        }

        const currentItemRow = this.getRowIdOfItem(currentItem);
        if (currentItemRow === null) {
            return;
        }

        // Start from the index of current item and iterate through next items

        // Search range
        let from = lastStartIndex + 1;
        let to = numberOfItems - 1;
        let direction = 1;
        let numberOfItemsToSearch = to - from + 1;

        if (upPressed) {
            from = lastStartIndex - 1;
            to = 0;
            direction = -1;
            numberOfItemsToSearch = from - to + 1;
        }

        // Result
        let nextItemIndex: number | null = null;
        let nextItemRow: number | null = null;
        let lastItemInCurrentRow : number = lastStartIndex;

        // Find the first item that has a different row value
        for (let i = 0; i < numberOfItemsToSearch; i++) {
            const index = from + i * direction;
            const item = this.getItemAtIndex(index);
            if (!item || item.node === null) {
                continue;
            }

            const itemRow = this.getRowIdOfItem(item);
            if (itemRow !== currentItemRow) {
                nextItemRow = itemRow;
                nextItemIndex = index;
                break;
            }

            lastItemInCurrentRow = index;
        }

        // There is no next row
        if (nextItemIndex === null) {
            let itemToSelect: number | null = null;

            const firstItem = this.getItemAtIndex(0);
            const isCurrentItemInFirstRow = firstItem && currentItemRow === this.getRowIdOfItem(firstItem);
            if (!isCurrentItemInFirstRow && upPressed) {
                itemToSelect = lastItemInCurrentRow - 1;
            }

            const lastItem = this.getItemAtIndex(numberOfItems - 1);
            const isCurrentItemInLastRow = lastItem && currentItemRow === this.getRowIdOfItem(lastItem);
            if (!isCurrentItemInLastRow && downPressed) {
                itemToSelect = lastItemInCurrentRow + 1;
            }

            if (itemToSelect) {
                updatedSelection.add(itemToSelect);
                updatedLastStartIndex = itemToSelect;

                this.setState({
                    selection: updatedSelection,
                    lastStartIndex: updatedLastStartIndex
                });

                onSelectionUpdate?.(updatedSelection);
                this.handleScrollToItem(updatedLastStartIndex);
            }

            return;
        }

        // Search range
        from = nextItemIndex;
        to = numberOfItems - 1;
        direction = 1;
        numberOfItemsToSearch = to - from + 1;

        if (upPressed) {
            from = nextItemIndex;
            to = 0;
            direction = -1;
            numberOfItemsToSearch = from - to + 1;
        }

        // Result
        let closestItem: MultiSelectGridItem | null = null;
        let closestDistance = Infinity;

        // Find the most closest item to the current item in the next row
        const hitCoordinate = this.getHitCoordinateOfItem(currentItem);
        for (let i = 0; i < numberOfItemsToSearch; i++) {
            const index = from + i * direction;
            const item = this.getItemAtIndex(index);
            if (!item || item.node === null) {
                continue;
            }

            const itemRow = this.getRowIdOfItem(item);
            if (nextItemRow !== itemRow) {
                break;
            }

            if (this.isHitCoordinateInsideItem(item, hitCoordinate)) {
                closestItem = item;
                break;
            }

            const { right } = item.node.getBoundingClientRect();
            const distance = Math.abs(right - hitCoordinate);
            if (distance <= closestDistance) {
                closestItem = item;
                closestDistance = distance;
            }
        }

        if (closestItem === null) {
            return;
        }

        updatedSelection.add(closestItem.selectableId);
        updatedLastStartIndex = closestItem.selectableId;

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);
        this.handleScrollToItem(updatedLastStartIndex);
    }, this.navigationDelay);

    handleSelectionOpen = (ev: KeyboardEvent) => {
        const { onSelectionOpen } = this.props;
        const { selection } = this.state;

        onSelectionOpen?.(ev, selection);
    };

    handleDocumentKeyDown = (ev: KeyboardEvent) => {
        const { lastStartIndex } = this.state;
        const {
            cmdPressed,
            aPressed,
            upPressed,
            downPressed,
            leftPressed,
            rightPressed,
            enterPressed
        } = getKeyboardInfo(ev);

        if (!this.keyboardNavigationEnabled) {
            return;
        }

        // Handle select all shortcut
        if (cmdPressed && aPressed) {
            this.handleSelectAllItems();
            ev.preventDefault();
            return;
        }

        if ((upPressed || downPressed) && lastStartIndex !== null) {
            // Because handleVerticalNavigation is throttled, we need to preventDefault here
            this.handleVerticalNavigation(ev);
            ev.preventDefault();
            return;
        }

        if ((leftPressed || rightPressed) && lastStartIndex !== null) {
            // Because handleHorizontalNavigation is throttled, we need to preventDefault here
            this.handleHorizontalNavigation(ev);
            ev.preventDefault();
            return;
        }

        if (enterPressed) {
            this.handleSelectionOpen(ev);
            ev.preventDefault();
        }
    };

    handleDocumentClick = () => {
        this.handleClearSelection();
    };

    handleScrollToItem = (index: number) => {
        const { scrollOffset, horizontalScroll, getScrollContainer, onScrollToUnmountedItem } = this.props;

        const element = this.getItemAtIndex(index)?.node;
        const container = getScrollContainer();

        if (!element) {
            onScrollToUnmountedItem?.(index);
            return;
        }

        scrollIfNecessary(element, { container, offset: scrollOffset, withSetTimeout: false, horizontalScroll });
    };

    handleScrollContainerFocus = () => {
        this.keyboardNavigationEnabled = true;
    };

    handleScrollContainerBlur = () => {
        this.keyboardNavigationEnabled = false;
    };

    handleGridItemMouseDown = (ev: MouseEvent, index: number) => {
        const { selection } = this.state;
        const itemSelected = selection.has(index);

        if (itemSelected) {
            // This prevents selection from being cleared
            ev.stopPropagation();
        }
    };

    handleGridItemSelect = (ev: MouseEvent, index: number) => {
        const { disableMultiselect, onSelectionUpdate, getScrollContainer } = this.props;
        const { selection, lastStartIndex } = this.state;
        const { cmdPressed, shiftPressed } = getKeyboardInfo(ev);

        getScrollContainer()?.focus();

        if (cmdPressed && selection.has(index)) {
            this.handleUnselectItem(index);
            return;
        }

        // Initialize updated state
        let updatedSelection = new Set<number>();
        let updatedLastStartIndex: number | null = index;

        // When cmd or shift is pressed, we want to include the previous selection
        if ((cmdPressed || shiftPressed) && !disableMultiselect) {
            updatedSelection = new Set<number>(selection);
        }

        updatedSelection.add(index);

        if (shiftPressed) {
            if (selection.has(index)) {
                updatedSelection.delete(index);
                // Keep the start of the range the same
                updatedLastStartIndex = lastStartIndex;

                if (index === lastStartIndex) {
                    // When first item of the range is unselected, update the range info
                    if (updatedSelection.size > 0) {
                        updatedLastStartIndex = Math.min(...updatedSelection);
                    } else {
                        updatedLastStartIndex = null;
                    }
                }
            } else if (lastStartIndex !== null && !disableMultiselect) {
                selectItemsInRange(updatedSelection, lastStartIndex, index);
                updatedLastStartIndex = lastStartIndex;
                clearNativeSelection();
            }
            // else -> Single item is selected, nothing to do extra here
        }

        this.setState({
            selection: updatedSelection,
            lastStartIndex: updatedLastStartIndex
        });

        onSelectionUpdate?.(updatedSelection);
    };

    handleGridItemDeselect = (index: number) => {
        const { selection } = this.state;

        if (selection.has(index)) {
            this.handleUnselectItem(index);
        }
    };

    override componentDidMount() {
        const { disableMultiselect, getScrollContainer } = this.props;

        const container = getScrollContainer();
        container?.addEventListener("focus", this.handleScrollContainerFocus);
        container?.addEventListener("blur", this.handleScrollContainerBlur);

        document.addEventListener("keydown", this.handleDocumentKeyDown);

        if (disableMultiselect) {
            document.addEventListener("click", this.handleDocumentClick);
        } else {
            document.removeEventListener("click", this.handleDocumentClick);
        }
    }

    override componentDidUpdate(prevProps: MultiSelectGridProps) {
        const { selectedItems: prevSelectedItems = [] } = prevProps;
        const { selectedItems = [], disableMultiselect, getScrollContainer } = this.props;
        const { selection } = this.state;

        // When an outside source changes the selected items, we need to update the selection state
        const selectedItemsChanged = shallowDiffer(selectedItems, prevSelectedItems);
        // We want to make sure that the new selectedItems are actually different from the current selection
        const selectedItemsAreDifferent = shallowDiffer(selectedItems, [...selection]);

        if (selectedItemsChanged && selectedItemsAreDifferent) {
            const updatedLastStartIndex = selectedItems.length > 0 ? selectedItems[0] : null;

            this.setState({
                selection: new Set(selectedItems),
                lastStartIndex: updatedLastStartIndex
            });

            getScrollContainer()?.focus();
        }

        if (disableMultiselect) {
            document.addEventListener("click", this.handleDocumentClick);
        } else {
            document.removeEventListener("click", this.handleDocumentClick);
        }
    }

    override componentWillUnmount() {
        const { getScrollContainer } = this.props;

        const container = getScrollContainer();
        container?.removeEventListener("focus", this.handleScrollContainerFocus);
        container?.removeEventListener("blur", this.handleScrollContainerBlur);

        document.removeEventListener("keydown", this.handleDocumentKeyDown);
        document.removeEventListener("click", this.handleDocumentClick);
    }

    render() {
        const {
            class: cssClass,
            children,
            disableMultiselect,
            getScrollContainer,
            onSelectionStart,
            onSelectionEnd
        } = this.props;

        const { selection } = this.state;

        const contextValue: MultiSelectGridContext = {
            onGridItemSelect: this.handleGridItemSelect,
            onGridItemDeselect: this.handleGridItemDeselect,
            onGridItemMouseDown: this.handleGridItemMouseDown
        };

        return (
            <MultiSelectGridContext.Provider value={contextValue}>
                <MultiSelectGroup
                    class={cssClass}
                    ref={this.multiSelectGroupRef}
                    selectedIds={[...selection]}
                    allowCtrlClick
                    allowShiftClick
                    deselectOnEsc
                    enableDeselect
                    resetOnStart
                    disabled={disableMultiselect}
                    getScrollContainer={getScrollContainer}
                    onSelectionDone={this.handleSelectionDone}
                    onClearSelection={this.handleClearSelection}
                    onSelectionStart={onSelectionStart}
                    onSelectionEnd={onSelectionEnd}>
                    {children}
                </MultiSelectGroup>
            </MultiSelectGridContext.Provider>
        );
    }
}

// #endregion

// #region - MultiSelectGridContext

interface MultiSelectGridContext {
    onGridItemSelect: (ev: MouseEvent, index: number) => void;
    onGridItemDeselect: (index: number) => void;
    onGridItemMouseDown: (ev: MouseEvent, index: number) => void;
}

const MultiSelectGridContextDefaultValue: MultiSelectGridContext = {
    onGridItemSelect: () => { /* No-op */ },
    onGridItemDeselect: () => { /* No-op */ },
    onGridItemMouseDown: () => { /* No-op */ }
};

const MultiSelectGridContext = createContext<MultiSelectGridContext>(MultiSelectGridContextDefaultValue);
const useMultiSelectGridContext = (): MultiSelectGridContext => useContext(MultiSelectGridContext)!;

// #endregion

// #region - MultiSelectGridItem

type MultiSelectGridItemRequiredProps = WithMultiSelectItemProps<number>;

interface MultiSelectGridItemInjectedProps {
    onGridItemMouseDown?: (ev: MouseEvent) => void;
    onGridItemSelect?: (ev: MouseEvent) => void;
    onGridItemDeselect?: () => void;
}

type WithMultiSelectGridItemProps = MultiSelectGridItemRequiredProps & MultiSelectGridItemInjectedProps;

/**
 * P is the type of own props of the wrapped component
 */
function withMultiSelectGridItem<P extends MultiSelectGridItemRequiredProps>(
    WrappedComponent: ComponentType<P>
) {
    return withMultiSelectItem((props: Omit<P, keyof MultiSelectGridItemInjectedProps>) => {
        const { selectableId: index } = props;

        const {
            onGridItemSelect,
            onGridItemDeselect,
            onGridItemMouseDown
        } = useMultiSelectGridContext();

        function handleMouseDown(ev: MouseEvent) {
            onGridItemMouseDown(ev, index);
        }

        function handleSelect(ev: MouseEvent) {
            onGridItemSelect(ev, index);
        }

        function handleDeselect() {
            onGridItemDeselect(index);
        }

        return (
            <WrappedComponent
                {...props as P}
                onGridItemMouseDown={handleMouseDown}
                onGridItemSelect={handleSelect}
                onGridItemDeselect={handleDeselect}/>
        );
    });
}

// #endregion

export {
    MultiSelectGrid,
    withMultiSelectGridItem,
    WithMultiSelectGridItemProps
};
